import type { LocationItem, LocationLevel, Lookups } from '@re/form-ui';
import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { LookupRow } from './types';

/** Active master data shaped like the org/public `/lookups` endpoint (for the builder preview). */
export function usePlatformLookups() {
  return useQuery({
    queryKey: ['builder-lookups'],
    queryFn: async (): Promise<Lookups> => {
      const [rt, cats, types, feats, groups] = await Promise.all([
        api<{ items: LookupRow[] }>('/request-types'),
        api<{ items: LookupRow[] }>('/property-categories'),
        api<{ items: LookupRow[] }>('/property-types'),
        api<{ items: LookupRow[] }>('/property-features'),
        api<{ items: LookupRow[] }>('/feature-groups').catch(() => ({ items: [] as LookupRow[] })),
      ]);
      const featById = new Map(feats.items.map((f) => [f.id, f]));
      return {
        requestTypes: rt.items.filter((r) => r.is_active !== false).map((r) => ({ id: r.id, code: r.code, name_ar: r.name_ar, name_en: r.name_en, direction: r.direction! })),
        categories: cats.items.filter((r) => r.is_active !== false).map((r) => ({ id: r.id, code: r.code, name_ar: r.name_ar, name_en: r.name_en })),
        propertyTypes: types.items.filter((r) => r.is_active !== false).map((r) => ({ id: r.id, code: r.code, name_ar: r.name_ar, name_en: r.name_en, category_id: r.category_id! })),
        // a detail applies wherever its feature does (as the org/public lookups)
        features: feats.items
          .filter((r) => r.is_active !== false)
          .map((r) => ({
            id: r.id, code: r.code, name_ar: r.name_ar, name_en: r.name_en, group_id: r.group_id ?? null, parent_id: r.parent_id ?? null,
            property_type_ids: (r.parent_id ? featById.get(r.parent_id)?.property_type_ids : r.property_type_ids) ?? [],
          })),
        featureGroups: groups.items.filter((r) => r.is_active !== false).map((r) => ({ id: r.id, code: r.code, name_ar: r.name_ar, name_en: r.name_en })),
      };
    },
    staleTime: 60_000,
  });
}

export async function loadLocations(level: LocationLevel, parentId?: number): Promise<LocationItem[]> {
  const r = await api<{ items: LocationItem[] }>(`/${level}`, { query: { parentId, includeInactive: false } });
  return r.items;
}
